import { Component } from '@angular/core';

export type Lang = 'en' | 'es';

@Component({
  selector: 'app-terms',
  templateUrl: './terms.component.html',
  styleUrls: ['./terms.component.css']
})
export class TermsComponent {

  isNavOpen = false;
  lang: Lang = 'en';

  setLang(l: Lang) {
    this.lang = l;
  }

  get t() {
    return this.translations[this.lang];
  }

  translations = {
    en: {
      nav: {
        login: 'LOGIN',
        register: 'REGISTER NOW',
      },
      hero: {
        eyebrow: 'LEGAL & PLATFORM POLICIES',
        title: 'TERMS &',
        titleHighlight: 'CONDITIONS.',
        subtitle: 'Please read these terms carefully before participating in any UMYO Español ecosystem. By joining, you agree to the following policies governing expression, conduct, and platform participation.'
      },
      meta: 'LAST UPDATED · UMYO ESPAÑOL PLATFORM POLICIES',
      sections: [
        {
          num: '01',
          title: 'Expression and Ecosystem Scope',
          desc: 'The Platform operates as a collection of <strong>mission-specific digital ecosystems</strong>. Each ecosystem functions as a purpose-driven private online association with a defined scope of discussion.',
          points: [
            'Members are encouraged to engage in open expression within the stated mission and intended purpose of the ecosystem they have joined. Expression is permitted to the extent it aligns with the ecosystem\'s defined focus and participation standards.',
            'Content, discussions, or activities that fall outside the stated mission or scope of a particular ecosystem may be moderated, restricted, redirected, or removed at the Company\'s discretion.',
            'The Company does not regulate expression based on viewpoint alone. Moderation decisions are based on alignment with ecosystem purpose, adherence to participation standards, and compliance with Platform-wide policies.',
            'Participation in any ecosystem is conditional and subject to continued compliance with applicable guidelines. Access to an ecosystem is a <strong>revocable license</strong> and may be suspended or terminated for violations of its stated mission, conduct standards, or Platform policies.',
            'Membership in one ecosystem does not grant rights, privileges, or expression authority within any other ecosystem.'
          ],
          highlight: 'UMYO operates multiple mission-specific private digital ecosystems. Participation is voluntary and conditioned upon agreement to the mission and standards of each ecosystem. The platform does not guarantee unrestricted expression.',
          list: [
            'Content may be moderated or removed if deemed outside the mission scope of the selected ecosystem, regardless of viewpoint.',
            'Mission-aligned expression: Speech is free within the mission of the ecosystem — not free across all topics everywhere.'
          ]
        },
        {
          num: '02',
          title: 'Non-Discrimination and Mission Alignment',
          desc: 'The Company provides access to the Platform <strong>without discrimination</strong> based on race, color, religion, sex, gender identity, sexual orientation, national origin, disability, political affiliation, or other legally protected characteristics.',
          points: [
            'Access to individual ecosystems is conditioned upon agreement to and compliance with that ecosystem\'s stated mission, scope, and participation standards. Moderation decisions are based on:'
          ],
          list: [
            'Conduct and behavior within the ecosystem',
            'Mission alignment with the stated ecosystem purpose',
            'Adherence to Platform-wide policies',
            'Not on a member\'s personal identity or protected characteristics'
          ],
          note: 'The Company reserves the right to suspend or terminate participation in any ecosystem where a member\'s conduct is inconsistent with its stated purpose or applicable guidelines.'
        },
        {
          num: '03',
          title: 'Member Appeals and Review Process',
          desc: 'Members who believe a moderation action was taken in error may submit a written appeal within <strong>fourteen (14) days</strong> of the action.',
          points: [
            'Appeals must include sufficient detail to allow review of the decision, including:'
          ],
          list: [
            'The relevant ecosystem name',
            'Date of the moderation action',
            'A description of the basis for the appeal'
          ],
          points2: [
            'Upon receipt of a valid appeal request, the Company will conduct an internal review of the moderation decision. Review may include evaluation of the original content, applicable ecosystem guidelines, and compliance with Platform policies.'
          ],
          highlight: 'The Company may uphold, modify, or reverse the original decision at its discretion. Decisions issued upon appeal are final.',
          note: 'Submission of an appeal does not guarantee reinstatement, refund, or restoration of access. During the review period, access may remain suspended. The Company reserves the right to decline repetitive, abusive, or bad-faith appeals.'
        }
      ],
      footer: {
        tagline: 'FOR US. BY US. POWERED BY UMYO.',
        quickLinks: 'QUICK LINKS',
        resources: 'RESOURCES',
        connect: 'LET\'S CONNECT',
        copyright: '© 2026 UMYO Español Directory. All Rights Reserved.'
      }
    },
    es: {
      nav: {
        login: 'INICIAR SESIÓN',
        register: 'REGISTRARSE AHORA',
      },
      hero: {
        eyebrow: 'POLÍTICAS LEGALES Y DE LA PLATAFORMA',
        title: 'TÉRMINOS Y',
        titleHighlight: 'CONDICIONES.',
        subtitle: 'Lea estos términos cuidadosamente antes de participar en cualquier ecosistema UMYO Español. Al unirse, acepta las siguientes políticas que rigen la expresión, la conducta y la participación en la plataforma.'
      },
      meta: 'ÚLTIMA ACTUALIZACIÓN · POLÍTICAS DE LA PLATAFORMA UMYO ESPAÑOL',
      sections: [
        {
          num: '01',
          title: 'Expresión y Alcance del Ecosistema',
          desc: 'La Plataforma opera como una colección de <strong>ecosistemas digitales específicos por misión</strong>. Cada ecosistema funciona como una asociación privada en línea impulsada por un propósito con un alcance de discusión definido.',
          points: [
            'Se anima a los miembros a participar en la expresión abierta dentro de la misión declarada y el propósito previsto del ecosistema al que se han unido. La expresión está permitida en la medida en que se alinee con el enfoque definido del ecosistema y los estándares de participación.',
            'El contenido, las discusiones o las actividades que queden fuera de la misión declarada o el alcance de un ecosistema en particular pueden ser moderados, restringidos, redirigidos o eliminados a discreción de la Compañía.',
            'La Compañía no regula la expresión basándose únicamente en el punto de vista. Las decisiones de moderación se basan en la alineación con el propósito del ecosistema, la adhesión a los estándares de participación y el cumplimiento de las políticas de toda la Plataforma.',
            'La participación en cualquier ecosistema es condicional y está sujeta al cumplimiento continuo de las pautas aplicables. El acceso a un ecosistema es una <strong>licencia revocable</strong> y puede ser suspendido o terminado por violaciones de su misión declarada, estándares de conducta o políticas de la Plataforma.',
            'La membresía en un ecosistema no otorga derechos, privilegios o autoridad de expresión dentro de ningún otro ecosistema.'
          ],
          highlight: 'UMYO opera múltiples ecosistemas digitales privados específicos por misión. La participación es voluntaria y está condicionada al acuerdo con la misión y los estándares de cada ecosistema. La plataforma no garantiza la expresión sin restricciones.',
          list: [
            'El contenido puede ser moderado o eliminado si se considera fuera del alcance de la misión del ecosistema seleccionado, independientemente del punto de vista.',
            'Expresión alineada con la misión: La expresión es libre dentro de la misión del ecosistema — no libre en todos los temas en todas partes.'
          ]
        },
        {
          num: '02',
          title: 'No Discriminación y Alineación con la Misión',
          desc: 'La Compañía proporciona acceso a la Plataforma <strong>sin discriminación</strong> por motivos de raza, color, religión, sexo, identidad de género, orientación sexual, origen nacional, discapacidad, afiliación política u otras características legalmente protegidas.',
          points: [
            'El acceso a ecosistemas individuales está condicionado al acuerdo y cumplimiento de la misión declarada, el alcance y los estándares de participación de ese ecosistema. Las decisiones de moderación se basan en:'
          ],
          list: [
            'Conducta y comportamiento dentro del ecosistema',
            'Alineación con la misión del propósito declarado del ecosistema',
            'Adhesión a las políticas de toda la Plataforma',
            'No en la identidad personal o características protegidas del miembro'
          ],
          note: 'La Compañía se reserva el derecho de suspender o terminar la participación en cualquier ecosistema donde la conducta de un miembro sea inconsistente con su propósito declarado o las pautas aplicables.'
        },
        {
          num: '03',
          title: 'Apelaciones de Miembros y Proceso de Revisión',
          desc: 'Los miembros que crean que se tomó una acción de moderación por error pueden presentar una apelación por escrito dentro de los <strong>catorce (14) días</strong> posteriores a la acción.',
          points: [
            'Las apelaciones deben incluir detalles suficientes para permitir la revisión de la decisión, incluyendo:'
          ],
          list: [
            'El nombre del ecosistema relevante',
            'Fecha de la acción de moderación',
            'Una descripción de la base para la apelación'
          ],
          points2: [
            'Al recibir una solicitud de apelación válida, la Compañía llevará a cabo una revisión interna de la decisión de moderación. La revisión puede incluir la evaluación del contenido original, las pautas del ecosistema aplicables y el cumplimiento de las políticas de la Plataforma.'
          ],
          highlight: 'La Compañía puede mantener, modificar o revertir la decisión original a su discreción. Las decisiones emitidas en apelación son definitivas.',
          note: 'La presentación de una apelación no garantiza la reinstalación, reembolso o restauración del acceso. Durante el período de revisión, el acceso puede permanecer suspendido. La Compañía se reserva el derecho de rechazar apelaciones repetitivas, abusivas o de mala fe.'
        }
      ],
      footer: {
        tagline: 'PARA NOSOTROS. POR NOSOTROS. POTENCIADO POR UMYO.',
        quickLinks: 'ENLACES RÁPIDOS',
        resources: 'RECURSOS',
        connect: 'CONECTEMOS',
        copyright: '© 2026 Directorio UMYO Español. Todos los derechos reservados.'
      }
    }
  };
}